import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Divider } from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { getHomeViewRole } from '@app/selectors/home';
import { changeClassRoom } from '@core/actions/classRoom';
import SpaceItem from '../Home/widgets/common/SpaceItem';
import SpaceListSkeleton from '../Home/widgets/common/SpaceListSkeleton';
import InfiniteScrollTrigger from '../common/InfiniteScrollTrigger';
import push from '@app/utils/routing';
import { trackHomeWidgetItemClicked } from '../Home/widgets/common/analytics';
import {
  NAVIGATION_SOURCE_SPACES,
  NAVIGATION_SOURCE_STARRED_SPACES_VIEW_ALL_MODAL,
  NAVIGATION_SOURCE_RECENTLY_VIEWED_SPACES_VIEW_ALL_MODAL,
} from '@core/constants/analytics';

interface SpacesListProps {
  spaces: any[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  starredIds: Set<string>;
  handleStarToggle: (id: string, index?: number) => void;
  hideEmptyState?: boolean;
  onClose?: () => void;
  isStaff?: boolean;
  listType?: 'all' | 'starred' | 'recent' | 'all_from_starred';
}

const SKELETON_KEYS = [
  'skeleton-0',
  'skeleton-1',
  'skeleton-2',
  'skeleton-3',
  'skeleton-4',
];

const SpacesList: React.FC<SpacesListProps> = ({
  spaces,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  starredIds,
  handleStarToggle,
  hideEmptyState = false,
  onClose,
  isStaff = false,
  listType = 'all',
}) => {
  const dispatch = useDispatch();
  const role = useSelector(getHomeViewRole);

  if (isLoading && spaces.length === 0) {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {SKELETON_KEYS.map((key, index) => (
          <React.Fragment key={key}>
            <SpaceListSkeleton />
            {index < 4 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    );
  }

  if (spaces.length === 0) {
    if (hideEmptyState) {
      return <Box minHeight="100px" />; // Retain some height to prevent visual collapse during exit animations
    }
    return (
      <Box p={4} textAlign="center">
        <Typography color="textSecondary">No spaces found.</Typography>
      </Box>
    );
  }

  const handleSpaceClick = React.useCallback(
    async (id: string, index?: number) => {
      let navigationSource = NAVIGATION_SOURCE_SPACES;
      if (listType === 'starred' || listType === 'all_from_starred')
        navigationSource = NAVIGATION_SOURCE_STARRED_SPACES_VIEW_ALL_MODAL;
      else if (listType === 'recent')
        navigationSource =
          NAVIGATION_SOURCE_RECENTLY_VIEWED_SPACES_VIEW_ALL_MODAL;

      trackHomeWidgetItemClicked({
        navigationSource,
        itemType: 'Spaces',
        itemId: id,
        spaceId: id,
        itemIndex: index,
        role,
      });

      // If we have access to lastNavigation, we could use it here.
      // For general space list, we route to the space's nav.
      const space = spaces.find((s) => s.id === id);
      if (onClose) {
        onClose();
      }
      if (space?.lastNavigation) {
        let navigationUrl = space.lastNavigation.replace(/#/, '');
        await dispatch(changeClassRoom(id) as any);
        push(navigationUrl);
      } else {
        push(`/dashboard/${id}/myspacenav`);
      }
    },
    [spaces, dispatch, onClose, listType, role]
  );

  const spacesCount = spaces.length;

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {spaces.map((space, index) => (
        <React.Fragment key={space.id}>
          <SpaceItem
            space={space}
            isStarred={starredIds.has(space.id)}
            onStarToggle={(id) => handleStarToggle(id, index)}
            onClick={(id) => handleSpaceClick(id, index)}
            isStaffAccess={space.isStaffAccess}
            isStaff={isStaff}
            archived={space.archived}
            isAssigned={space.isAssigned}
          />
          {index < spacesCount - 1 && <Divider />}
        </React.Fragment>
      ))}

      {isLoadingMore && (
        <Box mt={1}>
          <SpaceListSkeleton />
        </Box>
      )}

      <InfiniteScrollTrigger
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
      />
    </Box>
  );
};

SpacesList.propTypes = {
  spaces: PropTypes.arrayOf(PropTypes.any).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  starredIds: PropTypes.instanceOf(Set).isRequired,
  handleStarToggle: PropTypes.func.isRequired,
  hideEmptyState: PropTypes.bool,
  onClose: PropTypes.func,
};

export default SpacesList;
